import React, { useState, useEffect, createContext, useContext } from 'react';
import axios from 'axios';
import api from '../lib/api';
import { clearGAUserId, setGAUserId } from '../analytics';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      if (api && api.defaults && api.defaults.headers) {
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      }
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await api.get('/auth/me');
      setUser(response.data);
      if (response?.data?.id) {
        setGAUserId(response.data.id);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      // Do not hard-logout on transient 401s to avoid token flaps under load; surface loading=false and let views gate on user
      setUser(null);
      clearGAUserId();
    } finally {
      setLoading(false);
    }
  };

  const postAuthWithCapturedToken = (path, bearerToken) => {
    // IMPORTANT: do NOT rely on the `api` interceptor/localStorage token here.
    // We clear localStorage immediately on logout for UX, but axios interceptors
    // may run async and would strip the Authorization header if it reads after clear.
    try {
      if (!bearerToken) return;

      const baseURL =
        (api && api.defaults && api.defaults.baseURL) ||
        ((process.env.REACT_APP_BACKEND_URL || '') + '/api') ||
        '/api';

      const normalizedBase = String(baseURL).replace(/\/+$/, '');
      const normalizedPath = path.startsWith('/') ? path : `/${path}`;
      const url = `${normalizedBase}${normalizedPath}`;

      // fire-and-forget (best-effort); keepalive helps when navigating away.
      fetch(url, {
        method: 'POST',
        headers: { Authorization: `Bearer ${bearerToken}` },
        keepalive: true
      }).catch(() => {});
    } catch (_) {
      // Never block local logout on network errors.
    }
  };

  const clearLocalAuth = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    if (api && api.defaults && api.defaults.headers && api.defaults.headers.common) {
      delete api.defaults.headers.common['Authorization'];
    }
    clearGAUserId();
  };

  const login = (newToken, userData) => {
    setToken(newToken);
    setUser(userData);
    localStorage.setItem('token', newToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    if (api && api.defaults && api.defaults.headers) {
      api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    }
    if (userData?.id) {
      setGAUserId(userData.id);
    }
  };

  const logout = () => {
    const capturedToken = token;
    clearLocalAuth();
    // Best-effort: revoke the server-side session so stolen tokens stop working.
    postAuthWithCapturedToken('/auth/logout', capturedToken);
  };

  const logoutAll = () => {
    const capturedToken = token;
    clearLocalAuth();
    // Best-effort: revoke *all* sessions for the account in O(1) via server timestamp.
    postAuthWithCapturedToken('/auth/logout-all', capturedToken);
  };

  const updateUser = (updatedUserData) => {
    setUser(prevUser => {
      if (prevUser && prevUser.id === updatedUserData.id) {
        return { ...prevUser, ...updatedUserData };
      }
      return prevUser;
    });
  };

  return (
    <AuthContext.Provider value={{ user, token, login, logout, logoutAll, loading, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export default AuthContext;


